import {
  CheckCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
  StopOutlined,
  UserAddOutlined
} from '@ant-design/icons';
import type {
  MaterialPriceRow,
  SupplierAccountRequest,
  SupplierAccountRow,
  SupplierMaterialRow,
  SupplierMaterialSaveRequest,
  SupplierQualRow,
  SupplierQualSaveRequest,
  SupplierRow,
  SupplierSaveRequest
} from '@hq/api-sdk';
import { ApiError } from '@hq/api-sdk';
import { formatAmount, formatDisplayDate, formatPercent, percentToRatio } from '@hq/utils';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Drawer,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Segmented,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Tabs,
  Tag,
  Typography
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { type Dayjs } from 'dayjs';
import { useState } from 'react';
import { hq } from '../session';
import { sumWidth } from '@hq/ui-kit';

const PAGE_SIZE = 20;

/** 供应商/价格类枚举的中文映射：后端存英文码，列表与详情不得直接渲染原始码。 */
const SUPPLIER_TYPE_META: Record<string, string> = {
  OPERATOR: '运营主体',
  THIRD_PARTY: '第三方',
  MANUFACTURER: '生产厂商'
};

const SETTLE_TYPE_META: Record<string, string> = {
  MONTH: '月结',
  ORDER: '单结',
  CONSUME: '消耗结算'
};

const INVOICE_TYPE_META: Record<string, string> = {
  SPECIAL_VAT: '增值税专用发票',
  GENERAL_VAT: '增值税普通发票',
  ELECTRONIC: '电子发票'
};

const DELIVERY_MODE_META: Record<string, string> = {
  PLATFORM: '平台配送',
  SELF: '供应商自送',
  BOTH: '两者皆可'
};

const PRICE_TYPE_META: Record<string, string> = {
  PURCHASE: '采购价',
  SETTLE: '结算价',
  RETAIL: '零售价'
};

const TAX_KIND_META: Record<string, string> = {
  INCLUSIVE: '含税',
  EXCLUSIVE: '不含税'
};

const COOPERATE_META: Record<string, { text: string; color: string }> = {
  ACTIVE: { text: '合作中', color: 'green' },
  PENDING: { text: '待准入', color: 'gold' },
  TRIAL: { text: '试用', color: 'cyan' },
  SUSPENDED: { text: '暂停', color: 'orange' },
  STOPPED: { text: '暂停', color: 'orange' },
  BLACKLIST: { text: '黑名单', color: 'red' },
  TERMINATED: { text: '已终止', color: 'default' }
};

type CooperateFilter = 'ALL' | 'ACTIVE' | 'PENDING' | 'SUSPENDED' | 'BLACKLIST';

/**
 * 平台物资快搜：把“手填 materialId”换成按名称/编码搜索的下拉。
 * 对码写错不会报错，但会导致判源、供货、结算归一不到同一个物资，所以不给自由输入。
 * 编辑态物资不可改（后端是 supplier_id + material_id 唯一定位），直接用已加载的名称回显。
 */
function MaterialPicker({
  value,
  onChange,
  disabled,
  fallbackLabel
}: {
  value?: string;
  onChange?: (value?: string) => void;
  disabled?: boolean;
  fallbackLabel?: string;
}) {
  const [keyword, setKeyword] = useState('');
  const options = useQuery({
    queryKey: ['material-picker', keyword],
    queryFn: () => hq.material.list({ keyword: keyword || undefined, size: 20 }),
    enabled: !disabled
  });
  const items = (options.data?.items ?? []).map((row) => ({
    value: row.id,
    label: `${row.materialName ?? ''}（${row.materialCode ?? ''}）`
  }));
  return (
    <Select
      showSearch
      disabled={disabled}
      value={value}
      placeholder="搜索物资名称 / 编码"
      filterOption={false}
      onSearch={setKeyword}
      onChange={(next) => onChange?.(next)}
      loading={options.isFetching}
      options={disabled && value ? [{ value, label: fallbackLabel ?? value }, ...items] : items}
    />
  );
}

/** 供应商协同：准入状态、资质到期、SKU 映射与协议价（第三方协同前置，P4）。 */
export default function SupplierOps() {
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();
  const [keyword, setKeyword] = useState('');
  const [cooperate, setCooperate] = useState<CooperateFilter>('ALL');
  const [current, setCurrent] = useState<SupplierRow | null>(null);
  const [mappingOpen, setMappingOpen] = useState(false);
  const [editingMapping, setEditingMapping] = useState<SupplierMaterialRow | null>(null);
  const [form] = Form.useForm<SupplierMaterialSaveRequest>();

  // 供应商建档 / 准入 / 资质（R11 统一纳管：准入状态机 + 证照到期）
  const [supplierOpen, setSupplierOpen] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState<SupplierRow | null>(null);
  const [supplierForm] = Form.useForm<SupplierSaveRequest>();
  const [qualOpen, setQualOpen] = useState(false);
  const [editingQual, setEditingQual] = useState<SupplierQualRow | null>(null);
  const [qualForm] = Form.useForm<SupplierQualSaveRequest & { range?: [Dayjs, Dayjs] }>();
  const [admissionTarget, setAdmissionTarget] = useState<SupplierRow | null>(null);
  const [admissionForm] = Form.useForm<{ status: string; remark?: string }>();

  // 供应商门户账号开户：登录账号由服务端生成（机构号 + 4 位序号），这里只填资料与初始口令
  const [accountOpen, setAccountOpen] = useState(false);
  const [accountForm] = Form.useForm<SupplierAccountRequest>();

  const list = useQuery({
    queryKey: ['catalog', 'supplier', keyword, cooperate],
    placeholderData: keepPreviousData,
    queryFn: () =>
      hq.catalog.suppliers({
        size: PAGE_SIZE,
        keyword: keyword || undefined,
        cooperateStatus: cooperate === 'ALL' ? undefined : cooperate
      })
  });

  const detail = useQuery({
    queryKey: ['catalog', 'supplier', 'detail', current?.id],
    placeholderData: keepPreviousData,
    queryFn: () => hq.catalog.supplier(current?.id ?? ''),
    enabled: Boolean(current?.id)
  });

  const mappings = useQuery({
    queryKey: ['catalog', 'supplier', 'material', current?.id],
    placeholderData: keepPreviousData,
    queryFn: () => hq.catalog.supplierMaterials(current?.id ?? '', { size: 100 }),
    enabled: Boolean(current?.id)
  });

  const prices = useQuery({
    queryKey: ['catalog', 'supplier', 'price', current?.id],
    placeholderData: keepPreviousData,
    queryFn: () => hq.catalog.prices({ supplierId: current?.id }),
    enabled: Boolean(current?.id)
  });

  const saveMapping = useMutation({
    mutationFn: (values: SupplierMaterialSaveRequest) =>
      hq.catalog.saveSupplierMaterial(current?.id ?? '', values),
    onSuccess: async () => {
      message.success('SKU 映射已保存');
      setMappingOpen(false);
      form.resetFields();
      await queryClient.invalidateQueries({ queryKey: ['catalog', 'supplier', 'material'] });
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '保存失败')
  });

  const removeMapping = useMutation({
    mutationFn: (id: string) => hq.catalog.removeSupplierMaterial(id),
    onSuccess: async () => {
      message.success('已删除映射');
      await queryClient.invalidateQueries({ queryKey: ['catalog', 'supplier', 'material'] });
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '删除失败')
  });

  const saveSupplier = useMutation({
    mutationFn: (values: SupplierSaveRequest) => hq.catalog.saveSupplier(values),
    onSuccess: async (result) => {
      message.success(editingSupplier ? '供应商档案已更新' : `供应商已建档（编码 ${result.supplierCode ?? '-'}），待准入`);
      setSupplierOpen(false);
      setEditingSupplier(null);
      supplierForm.resetFields();
      await queryClient.invalidateQueries({ queryKey: ['catalog', 'supplier'] });
      await detail.refetch();
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '保存失败')
  });

  const openAccount = useMutation({
    mutationFn: (values: SupplierAccountRequest) =>
      hq.catalog.openSupplierAccount(current?.id ?? '', values),
    onSuccess: async (result) => {
      setAccountOpen(false);
      accountForm.resetFields();
      // 账号由系统生成，只在开通结果里出现这一次：常驻弹窗回显，由用户手工关闭
      modal.success({
        title: '供应商门户账号已开通',
        width: 480,
        okText: '我已记录，关闭',
        content: (
          <div style={{ lineHeight: 1.9 }}>
            <div>
              登录账号：
              <span style={{ fontFamily: 'monospace', fontWeight: 600, fontSize: 15 }}>
                {result.userCode}
              </span>
            </div>
            <div style={{ color: 'rgba(0,0,0,0.45)' }}>
              请把登录账号与本次填写的初始口令转交供应商；口令 90 天有效。
            </div>
          </div>
        )
      });
      await queryClient.invalidateQueries({ queryKey: ['catalog', 'supplier'] });
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '开户失败')
  });

  // 门户账号维护：服务端同时按「租户 + supplier_id」过滤，运营端只能看到自己名下供应商的账号
  const accountsKey = ['catalog', 'supplier', current?.id, 'accounts'] as const;
  const accounts = useQuery({
    queryKey: accountsKey,
    queryFn: () => hq.catalog.supplierAccounts(current?.id ?? ''),
    enabled: Boolean(current?.id)
  });

  const resetAccountPwd = useMutation({
    mutationFn: (userId: string) => hq.catalog.resetSupplierAccountPassword(current?.id ?? '', userId),
    onSuccess: async (result) => {
      modal.success({
        title: result.generated ? '已生成一次性口令' : '口令已重置',
        width: 460,
        okText: '我已记录，关闭',
        content: (
          <div style={{ lineHeight: 1.9 }}>
            <div>
              新口令：
              <span style={{ fontFamily: 'monospace', fontWeight: 600, fontSize: 15 }}>{result.password}</span>
            </div>
            <div style={{ color: 'rgba(0,0,0,0.45)' }}>
              仅本次回显，请转交供应商；有效期 90 天，建议首次登录后自行修改。
            </div>
          </div>
        )
      });
      await queryClient.invalidateQueries({ queryKey: accountsKey });
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '重置失败')
  });

  const toggleAccount = useMutation({
    mutationFn: (payload: { userId: string; status: 'ENABLED' | 'DISABLED' }) =>
      hq.catalog.setSupplierAccountStatus(current?.id ?? '', payload.userId, payload.status),
    onSuccess: async () => {
      message.success('账号状态已更新');
      await queryClient.invalidateQueries({ queryKey: accountsKey });
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '操作失败')
  });

  const unbindAccount = useMutation({
    mutationFn: (userId: string) => hq.catalog.unbindSupplierAccount(current?.id ?? '', userId),
    onSuccess: async () => {
      message.success('已解绑，该账号随即失去供应商门户权限');
      await queryClient.invalidateQueries({ queryKey: accountsKey });
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '解绑失败')
  });

  const admit = useMutation({
    mutationFn: (payload: { id: string; status: string; remark?: string }) =>
      hq.catalog.admitSupplier(payload.id, { status: payload.status, remark: payload.remark }),
    onSuccess: async (result) => {
      message.success(`准入状态已更新为 ${COOPERATE_META[result.cooperateStatus]?.text ?? result.cooperateStatus}`);
      await queryClient.invalidateQueries({ queryKey: ['catalog', 'supplier'] });
      await detail.refetch();
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '状态更新失败')
  });

  const saveQual = useMutation({
    mutationFn: (values: SupplierQualSaveRequest & { range?: [Dayjs, Dayjs] }) => {
      const body: SupplierQualSaveRequest = {
        ...values,
        issueAt: values.range?.[0]?.format('YYYY-MM-DD') ?? values.issueAt ?? null,
        expireAt: values.range?.[1]?.format('YYYY-MM-DD') ?? values.expireAt ?? null
      };
      delete (body as { range?: unknown }).range;
      return hq.catalog.saveQual(current?.id ?? '', body);
    },
    onSuccess: async () => {
      message.success('资质已保存');
      setQualOpen(false);
      setEditingQual(null);
      qualForm.resetFields();
      await detail.refetch();
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '资质保存失败')
  });

  const removeQual = useMutation({
    mutationFn: (id: string) => hq.catalog.removeQual(id),
    onSuccess: async () => {
      message.success('资质已删除');
      await detail.refetch();
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '删除失败')
  });

  const openSupplier = (row?: SupplierRow) => {
    setEditingSupplier(row ?? null);
    supplierForm.resetFields();
    if (row) {
      supplierForm.setFieldsValue({
        id: row.id,
        supplierName: row.supplierName,
        supplierType: row.supplierType ?? 'THIRD_PARTY',
        creditCode: row.creditCode,
        legalPerson: row.legalPerson,
        contactPerson: row.contactPerson,
        contactPhone: row.contactPhone,
        address: row.address,
        settleType: row.settleType ?? 'MONTH',
        invoiceType: row.invoiceType,
        // 后端 0~1 → 表单百分数（0.13 → 13）
        taxRate: row.taxRate == null ? undefined : Number((row.taxRate * 100).toFixed(2)),
        deliveryMode: row.deliveryMode ?? 'PLATFORM',
        useOuterWarehouse: row.useOuterWarehouse,
        apiEnabled: row.apiEnabled,
        remark: row.remark
      });
    } else {
      supplierForm.setFieldsValue({ supplierType: 'THIRD_PARTY', settleType: 'MONTH', deliveryMode: 'PLATFORM', taxRate: 13 });
    }
    setSupplierOpen(true);
  };

  const openAdmission = (row: SupplierRow) => {
    setAdmissionTarget(row);
    admissionForm.setFieldsValue({
      status: row.cooperateStatus === 'ACTIVE' ? 'SUSPENDED' : 'ACTIVE',
      remark: ''
    });
  };

  const openQual = (row?: SupplierQualRow) => {
    setEditingQual(row ?? null);
    qualForm.resetFields();
    if (row) {
      qualForm.setFieldsValue({
        qualType: row.qualType ?? '',
        qualNo: row.qualNo ?? '',
        fileUrl: row.fileUrl ?? '',
        remindDays: row.remindDays,
        range: [
          row.issueAt ? dayjs(row.issueAt) : dayjs(),
          row.expireAt ? dayjs(row.expireAt) : dayjs().add(1, 'year')
        ]
      });
    } else {
      qualForm.setFieldsValue({ remindDays: 30, range: [dayjs(), dayjs().add(1, 'year')] });
    }
    setQualOpen(true);
  };

  const columns: ColumnsType<SupplierRow> = [
    {
      title: '供应商编码',
      dataIndex: 'supplierCode',
      width: 140,
      render: (value: string, row) => <Typography.Link onClick={() => setCurrent(row)}>{value}</Typography.Link>
    },
    { title: '供应商名称', dataIndex: 'supplierName', width: 240, ellipsis: true },
    {
      title: '类型',
      dataIndex: 'supplierType',
      width: 110,
      render: (value: string | null) => (value ? SUPPLIER_TYPE_META[value] ?? value : '-')
    },
    { title: '联系人', dataIndex: 'contactPerson', width: 110, render: (value: string | null) => value || '-' },
    { title: '联系电话', dataIndex: 'contactPhone', width: 140, render: (value: string | null) => value || '-' },
    {
      title: '结算方式',
      dataIndex: 'settleType',
      width: 110,
      render: (value: string | null) => (value ? SETTLE_TYPE_META[value] ?? value : '-')
    },
    // 后端 tax_rate 统一 0~1（0.13 = 13%），必须 ×100 展示
    { title: '税率', dataIndex: 'taxRate', width: 90, align: 'right', render: (value: number) => formatPercent(value) },
    {
      title: '评分',
      dataIndex: 'score',
      width: 90,
      align: 'right',
      render: (value: number) => (value > 0 ? value.toFixed(1) : '-')
    },
    {
      title: '合作状态',
      dataIndex: 'cooperateStatus',
      width: 110,
      render: (value: string | null) => {
        if (!value) return '-';
        const meta = COOPERATE_META[value] ?? { text: value, color: 'default' };
        return <Tag color={meta.color}>{meta.text}</Tag>;
      }
    },
    {
      title: '外部仓',
      dataIndex: 'useOuterWarehouse',
      width: 90,
      render: (value: boolean) => (value ? <Tag color="blue">是</Tag> : '-')
    },
    {
      title: '操作',
      key: 'action',
      width: 160,
      fixed: 'right',
      render: (_value, row) => (
        <Space size={4}>
          <Button size="small" type="link" icon={<EditOutlined />} onClick={() => openSupplier(row)}>
            编辑
          </Button>
          <Button size="small" type="link" onClick={() => openAdmission(row)}>
            准入
          </Button>
        </Space>
      )
    }
  ];

  const mappingColumns: ColumnsType<SupplierMaterialRow> = [
    { title: '平台物资编码', dataIndex: 'materialCode', width: 150, render: (value: string | null) => value || '-' },
    { title: '平台物资名称', dataIndex: 'materialName', width: 200, ellipsis: true },
    { title: '供方 SKU', dataIndex: 'supplierSkuCode', width: 140, render: (value: string | null) => value || '-' },
    { title: '供方品名', dataIndex: 'supplierSkuName', width: 160, render: (value: string | null) => value || '-' },
    { title: '条码', dataIndex: 'supplierBarcode', width: 140, render: (value: string | null) => value || '-' },
    { title: '型号', dataIndex: 'supplierModel', width: 120, render: (value: string | null) => value || '-' },
    { title: '时效(h)', dataIndex: 'leadTimeHours', width: 90, align: 'right' },
    { title: '起订量', dataIndex: 'moq', width: 90, align: 'right' },
    {
      title: '首选',
      dataIndex: 'isPreferred',
      width: 80,
      render: (value: boolean) => (value ? <Tag color="green">首选</Tag> : '-')
    },
    {
      title: '操作',
      key: 'action',
      width: 130,
      fixed: 'right',
      render: (_value, row) => (
        <Space size={4}>
          <Button
            size="small"
            type="link"
            icon={<EditOutlined />}
            onClick={() => {
              setEditingMapping(row);
              form.setFieldsValue({
                materialId: row.materialId,
                supplierSkuCode: row.supplierSkuCode ?? '',
                supplierSkuName: row.supplierSkuName ?? '',
                supplierBarcode: row.supplierBarcode ?? '',
                supplierModel: row.supplierModel ?? '',
                supplierSpec: row.supplierSpec ?? '',
                originPlace: row.originPlace ?? '',
                leadTimeHours: row.leadTimeHours,
                moq: row.moq,
                packQty: row.packQty,
                isPreferred: row.isPreferred,
                status: row.status
              });
              setMappingOpen(true);
            }}
          >
            编辑
          </Button>
          <Button
            size="small"
            type="link"
            danger
            icon={<DeleteOutlined />}
            onClick={() =>
              modal.confirm({
                title: '删除 SKU 映射？',
                content: '删除后该物资无法按供方编码对码，可能影响判源与结算。',
                okText: '删除',
                cancelText: '取消',
                onOk: () => removeMapping.mutateAsync(row.id)
              })
            }
          />
        </Space>
      )
    }
  ];

  const priceColumns: ColumnsType<MaterialPriceRow> = [
    { title: '物资 ID', dataIndex: 'materialId', width: 200 },
    {
      title: '价格类型',
      dataIndex: 'priceType',
      width: 110,
      render: (value: string | null) => (value ? PRICE_TYPE_META[value] ?? value : '-')
    },
    {
      title: '含税',
      dataIndex: 'taxKind',
      width: 90,
      render: (value: string | null) => (value ? TAX_KIND_META[value] ?? value : '-')
    },
    { title: '税率', dataIndex: 'taxRate', width: 90, align: 'right', render: (value: number) => formatPercent(value) },
    { title: '单价', dataIndex: 'price', width: 130, align: 'right', render: (value: number) => formatAmount(value, 4) },
    { title: '生效起', dataIndex: 'effectiveFrom', width: 120, render: (value: string | null) => formatDisplayDate(value) },
    { title: '生效止', dataIndex: 'effectiveTo', width: 120, render: (value: string | null) => formatDisplayDate(value) },
    {
      title: '审批',
      dataIndex: 'approvalStatus',
      width: 110,
      render: (value: string | null) =>
        value === 'APPROVED' ? <Tag color="green">已批准</Tag> : <Tag>{value ?? '待审'}</Tag>
    }
  ];

  const quals = detail.data?.quals ?? [];
  const expiring = quals.filter((row) => {
    if (!row.expireAt) return false;
    const days = dayjs(row.expireAt).startOf('day').diff(dayjs().startOf('day'), 'day');
    return days <= 30;
  });

  return (
    <Card
      className="page-card"
      title={
        <Space>
          供应商协同
          <Typography.Text type="secondary">准入状态、资质到期、SKU 映射与协议价</Typography.Text>
        </Space>
      }
      extra={
        <Space>
          <Input.Search
            allowClear
            placeholder="名称 / 编码 / 联系人"
            style={{ width: 220 }}
            onSearch={(value) => setKeyword(value)}
          />
          <Button icon={<ReloadOutlined />} onClick={() => list.refetch()} loading={list.isFetching}>
            刷新
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => openSupplier()}>
            新增供应商
          </Button>
        </Space>
      }
    >
      <Segmented<CooperateFilter>
        value={cooperate}
        options={[
          { label: '全部', value: 'ALL' },
          { label: '合作中', value: 'ACTIVE' },
          { label: '待准入', value: 'PENDING' },
          { label: '暂停', value: 'SUSPENDED' },
          { label: '黑名单', value: 'BLACKLIST' }
        ]}
        onChange={(value) => setCooperate(value)}
        style={{ marginBottom: 12 }}
      />

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="供应商统一纳管（招标 R11）"
        description="新供应商建档后为「待准入」，审核通过转「合作中」；资质证照按到期日实时计算临期/过期，黑名单必须填写原因。"
      />

      {list.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="供应商加载失败"
          description={list.error instanceof ApiError ? list.error.friendlyMessage : String(list.error)}
        />
      ) : null}

      <Table<SupplierRow>
        rowKey="id"
        size="middle"
        columns={columns}
        dataSource={list.data?.items ?? []}
        loading={list.isPending}
        pagination={false}
        scroll={{ x: sumWidth(columns) }}
        locale={{ emptyText: '暂无供应商' }}
        onRow={(row) => ({ onDoubleClick: () => setCurrent(row), style: { cursor: 'pointer' } })}
      />

      <Drawer
        title={current ? `${current.supplierName}（${current.supplierCode}）` : '供应商详情'}
        open={current !== null}
        onClose={() => setCurrent(null)}
        width={1100}
        extra={
          current ? (
            <Space>
              <Button icon={<EditOutlined />} onClick={() => openSupplier(current)}>
                编辑档案
              </Button>
              <Button icon={<CheckCircleOutlined />} onClick={() => openAdmission(current)}>
                准入审核
              </Button>
              <Button
                icon={<UserAddOutlined />}
                onClick={() => {
                  accountForm.resetFields();
                  setAccountOpen(true);
                }}
              >
                开通门户账号
              </Button>
              {current.cooperateStatus === 'BLACKLIST' ? (
                <Button
                  icon={<StopOutlined />}
                  onClick={() =>
                    modal.confirm({
                      title: '恢复合作？',
                      content: '将状态改回「合作中」，请确认已完成整改。',
                      okText: '恢复',
                      onOk: () => admit.mutateAsync({ id: current.id, status: 'ACTIVE', remark: '黑名单整改完成，恢复合作' })
                    })
                  }
                >
                  恢复合作
                </Button>
              ) : null}
            </Space>
          ) : null
        }
      >
        {detail.isLoading ? (
          <Spin />
        ) : detail.isError ? (
          <Alert
            type="error"
            showIcon
            message="详情加载失败"
            description={detail.error instanceof ApiError ? detail.error.friendlyMessage : String(detail.error)}
          />
        ) : detail.data ? (
          <>
            <Descriptions size="small" column={3} bordered style={{ marginBottom: 16 }}>
              <Descriptions.Item label="信用代码">{detail.data.supplier.creditCode || '-'}</Descriptions.Item>
              <Descriptions.Item label="法人">{detail.data.supplier.legalPerson || '-'}</Descriptions.Item>
              <Descriptions.Item label="联系人">{detail.data.supplier.contactPerson || '-'}</Descriptions.Item>
              <Descriptions.Item label="电话">{detail.data.supplier.contactPhone || '-'}</Descriptions.Item>
              <Descriptions.Item label="结算方式">
                {SETTLE_TYPE_META[detail.data.supplier.settleType ?? ''] ?? detail.data.supplier.settleType ?? '-'}
              </Descriptions.Item>
              <Descriptions.Item label="发票类型">
                {INVOICE_TYPE_META[detail.data.supplier.invoiceType ?? ''] ?? detail.data.supplier.invoiceType ?? '-'}
              </Descriptions.Item>
              <Descriptions.Item label="配送方式">
                {DELIVERY_MODE_META[detail.data.supplier.deliveryMode ?? ''] ?? detail.data.supplier.deliveryMode ?? '-'}
              </Descriptions.Item>
              <Descriptions.Item label="接口对接">
                {detail.data.supplier.apiEnabled ? <Tag color="blue">已开启</Tag> : '未开启'}
              </Descriptions.Item>
              <Descriptions.Item label="合作状态">
                {(() => {
                  const meta = COOPERATE_META[detail.data.supplier.cooperateStatus ?? ''] ?? {
                    text: detail.data.supplier.cooperateStatus ?? '-',
                    color: 'default'
                  };
                  return <Tag color={meta.color}>{meta.text}</Tag>;
                })()}
              </Descriptions.Item>
              <Descriptions.Item label="地址" span={3}>
                {detail.data.supplier.address || '-'}
              </Descriptions.Item>
            </Descriptions>

            {expiring.length > 0 ? (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
                message={`${expiring.length} 项资质将在 30 天内到期`}
                description={expiring
                  .map((row) => `${row.qualType ?? row.qualNo ?? '资质'}（${formatDisplayDate(row.expireAt)}）`)
                  .join('、')}
              />
            ) : null}

            <Tabs
              items={[
                {
                  key: 'qual',
                  label: `资质（${quals.length}）`,
                  children: (
                    <>
                      <Space style={{ marginBottom: 12 }}>
                        <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => openQual()}>
                          新增资质
                        </Button>
                        <Typography.Text type="secondary">临期阈值按每条资质的提醒天数（默认 30 天）</Typography.Text>
                      </Space>
                      <Table
                        rowKey="id"
                        size="small"
                        pagination={false}
                        dataSource={quals}
                        columns={[
                          { title: '资质类型', dataIndex: 'qualType', width: 140, render: (v: string | null) => v || '-' },
                          { title: '证书号', dataIndex: 'qualNo', width: 180, render: (v: string | null) => v || '-' },
                          { title: '发证日', dataIndex: 'issueAt', width: 120, render: (v: string | null) => formatDisplayDate(v) },
                          {
                            title: '到期日',
                            dataIndex: 'expireAt',
                            width: 120,
                            render: (v: string | null) => {
                              if (!v) return '-';
                              const days = dayjs(v).startOf('day').diff(dayjs().startOf('day'), 'day');
                              if (days < 0) return <Tag color="red">已过期 {Math.abs(days)} 天</Tag>;
                              if (days <= 30) return <Tag color="orange">{formatDisplayDate(v)}（{days} 天）</Tag>;
                              return formatDisplayDate(v);
                            }
                          },
                          {
                            title: '状态',
                            dataIndex: 'status',
                            width: 100,
                            render: (v: string | null) =>
                              v === 'EXPIRED' ? <Tag color="red">已过期</Tag> : v === 'EXPIRING' ? <Tag color="orange">临期</Tag> : <Tag color="green">有效</Tag>
                          },
                          {
                            title: '操作',
                            key: 'action',
                            width: 130,
                            render: (_v, row) => (
                              <Space size={4}>
                                <Button size="small" type="link" icon={<EditOutlined />} onClick={() => openQual(row)}>
                                  编辑
                                </Button>
                                <Button
                                  size="small"
                                  type="link"
                                  danger
                                  icon={<DeleteOutlined />}
                                  onClick={() =>
                                    modal.confirm({
                                      title: '删除资质？',
                                      content: '删除后资质到期预警不再提示，请谨慎操作。',
                                      okText: '删除',
                                      onOk: () => removeQual.mutateAsync(row.id)
                                    })
                                  }
                                />
                              </Space>
                            )
                          }
                        ]}
                      />
                    </>
                  )
                },
                {
                  key: 'material',
                  label: `SKU 映射（${mappings.data?.items.length ?? 0}）`,
                  children: (
                    <>
                      <Space style={{ marginBottom: 12 }}>
                        <Button
                          type="primary"
                          size="small"
                          icon={<PlusOutlined />}
                          onClick={() => {
                            setEditingMapping(null);
                            form.setFieldsValue({
                              materialId: '',
                              supplierSkuCode: '',
                              supplierSkuName: '',
                              supplierBarcode: '',
                              supplierModel: '',
                              supplierSpec: '',
                              originPlace: '',
                              leadTimeHours: 24,
                              moq: 1,
                              packQty: 1,
                              isPreferred: false,
                              status: 'ACTIVE'
                            });
                            setMappingOpen(true);
                          }}
                        >
                          新增映射
                        </Button>
                        <Typography.Text type="secondary">
                          判源、供货、结算按 platform materialId 归一
                        </Typography.Text>
                      </Space>
                      <Table<SupplierMaterialRow>
                        rowKey="id"
                        size="small"
                        columns={mappingColumns}
                        dataSource={mappings.data?.items ?? []}
                        loading={Boolean(current?.id) && mappings.isPending}
                        pagination={false}
                        scroll={{ x: sumWidth(mappingColumns) }}
                        locale={{ emptyText: '尚未维护供方 SKU 映射' }}
                      />
                    </>
                  )
                },
                {
                  key: 'price',
                  label: `协议价（${prices.data?.length ?? 0}）`,
                  children: (
                    <Table<MaterialPriceRow>
                      rowKey="id"
                      size="small"
                      columns={priceColumns}
                      dataSource={prices.data ?? []}
                      loading={Boolean(current?.id) && prices.isPending}
                      pagination={false}
                      scroll={{ x: sumWidth(priceColumns) }}
                      locale={{ emptyText: '暂无价格协议' }}
                    />
                  )
                },
                {
                  key: 'account',
                  label: `门户账号（${accounts.data?.length ?? 0}）`,
                  children: (
                    <>
                      <Space style={{ marginBottom: 12 }}>
                        <Button
                          type="primary"
                          size="small"
                          icon={<UserAddOutlined />}
                          onClick={() => {
                            accountForm.resetFields();
                            setAccountOpen(true);
                          }}
                        >
                          开通账号
                        </Button>
                        <Typography.Text type="secondary">
                          账号由系统生成；只能维护本供应商名下账号
                        </Typography.Text>
                      </Space>
                      <Table<SupplierAccountRow>
                        rowKey="id"
                        size="small"
                        pagination={false}
                        loading={Boolean(current?.id) && accounts.isPending}
                        dataSource={accounts.data ?? []}
                        locale={{ emptyText: '尚未开通门户账号' }}
                        columns={[
                          { title: '登录账号', dataIndex: 'userCode', width: 190 },
                          { title: '姓名', dataIndex: 'realName', width: 110, render: (v: string | null) => v || '-' },
                          { title: '手机号', dataIndex: 'mobileMask', width: 130, render: (v: string | null) => v || '-' },
                          { title: '邮箱', dataIndex: 'email', width: 180, render: (v: string | null) => v || '-' },
                          {
                            title: '状态',
                            dataIndex: 'status',
                            width: 80,
                            render: (v: string) =>
                              v === 'ENABLED' ? <Tag color="green">启用</Tag> : <Tag color="red">停用</Tag>
                          },
                          {
                            title: '口令到期',
                            dataIndex: 'pwdExpireAt',
                            width: 110,
                            render: (v: string | null) => (v ? formatDisplayDate(v) : '-')
                          },
                          {
                            title: '最近登录',
                            dataIndex: 'lastLoginAt',
                            width: 140,
                            render: (v: string | null) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '-')
                          },
                          {
                            title: '操作',
                            key: 'op',
                            width: 210,
                            render: (_: unknown, row: SupplierAccountRow) => (
                              <Space size={0}>
                                <Button
                                  size="small"
                                  type="link"
                                  loading={resetAccountPwd.isPending && resetAccountPwd.variables === row.id}
                                  onClick={() =>
                                    modal.confirm({
                                      title: '重置登录口令？',
                                      content: `将为 ${row.userCode} 生成新口令，旧口令立即失效。`,
                                      okText: '重置',
                                      onOk: () => resetAccountPwd.mutateAsync(row.id)
                                    })
                                  }
                                >
                                  重置口令
                                </Button>
                                <Button
                                  size="small"
                                  type="link"
                                  onClick={() =>
                                    toggleAccount.mutate({
                                      userId: row.id,
                                      status: row.status === 'ENABLED' ? 'DISABLED' : 'ENABLED'
                                    })
                                  }
                                >
                                  {row.status === 'ENABLED' ? '停用' : '启用'}
                                </Button>
                                <Button
                                  size="small"
                                  type="link"
                                  danger
                                  onClick={() =>
                                    modal.confirm({
                                      title: '解绑该账号？',
                                      content: '解绑后该账号立即失去供应商门户权限并停用，账号与审计记录保留。',
                                      okText: '解绑',
                                      okButtonProps: { danger: true },
                                      onOk: () => unbindAccount.mutateAsync(row.id)
                                    })
                                  }
                                >
                                  解绑
                                </Button>
                              </Space>
                            )
                          }
                        ]}
                      />
                    </>
                  )
                }
              ]}
            />
          </>
        ) : null}
      </Drawer>

      <Modal
        title={editingMapping ? '编辑 SKU 映射' : '新增 SKU 映射'}
        open={mappingOpen}
        onCancel={() => setMappingOpen(false)}
        okText="保存"
        cancelText="取消"
        confirmLoading={saveMapping.isPending}
        onOk={() => form.submit()}
        width={720}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" onFinish={(values) => saveMapping.mutate(values)}>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="materialId" label="平台物资" rules={[{ required: true, message: '请选择平台物资' }]}>
                <MaterialPicker
                  disabled={editingMapping !== null}
                  fallbackLabel={`${editingMapping?.materialName ?? ''}（${editingMapping?.materialCode ?? ''}）`}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="supplierSkuCode" label="供方 SKU 编码" rules={[{ required: true, message: '请填写供方 SKU' }]}>
                <Input maxLength={64} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="supplierSkuName" label="供方品名">
                <Input maxLength={128} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="supplierBarcode" label="条码">
                <Input maxLength={64} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="supplierModel" label="型号">
                <Input maxLength={64} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="originPlace" label="产地">
                <Input maxLength={64} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="leadTimeHours" label="交货时效（小时）">
                <InputNumber min={1} max={720} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="moq" label="起订量">
                <InputNumber min={0} precision={4} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="packQty" label="包装量">
                <InputNumber min={1} precision={4} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="isPreferred" label="首选供应商" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="status" label="状态" tooltip="停用后该 SKU 不再参与判源与供货">
                <Select
                  options={[
                    { value: 'ACTIVE', label: '生效' },
                    { value: 'STOP', label: '停用' }
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      <Modal
        title={editingSupplier ? '编辑供应商档案' : '新增供应商'}
        open={supplierOpen}
        onCancel={() => setSupplierOpen(false)}
        okText="保存"
        cancelText="取消"
        confirmLoading={saveSupplier.isPending}
        onOk={() => supplierForm.submit()}
        width={760}
        destroyOnHidden
      >
        <Form
          form={supplierForm}
          layout="vertical"
          onFinish={(values) =>
            saveSupplier.mutate({
              ...values,
              id: editingSupplier?.id,
              // 表单是百分数（13），后端 tax_rate 是 0~1 小数
              taxRate: percentToRatio(values.taxRate as number | undefined)
            })
          }
        >
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="supplierName" label="供应商名称" rules={[{ required: true, message: '必填' }]}>
                <Input maxLength={128} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="供应商编码"
                tooltip="由系统生成（SUP + 3 位序号，租户内唯一），建档后不可修改"
              >
                <Input
                  readOnly
                  placeholder="保存后自动生成"
                  value={editingSupplier?.supplierCode ?? ''}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="supplierType" label="供应商类型">
                <Select
                  options={[
                    { value: 'OPERATOR', label: '运营商' },
                    { value: 'THIRD_PARTY', label: '第三方' },
                    { value: 'MANUFACTURER', label: '生产厂家' }
                  ]}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="settleType" label="结算方式">
                <Select
                  options={[
                    { value: 'MONTH', label: '月结' },
                    { value: 'ORDER', label: '单结' },
                    { value: 'CONSUME', label: '按消耗' }
                  ]}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="deliveryMode" label="配送方式">
                <Select
                  options={[
                    { value: 'PLATFORM', label: '平台配送' },
                    { value: 'SELF', label: '供应商自送' },
                    { value: 'BOTH', label: '两者皆可' }
                  ]}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="creditCode"
                label="统一社会信用代码"
                rules={[
                  {
                    // 18 位 USCC，字符集排除 I/O/S/V/Z（GB 32100-2015）
                    pattern: /^[0-9A-HJ-NPQRTUWXY]{18}$/i,
                    message: '应为 18 位统一社会信用代码（数字或大写字母）'
                  }
                ]}
              >
                <Input maxLength={18} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="legalPerson" label="法人">
                <Input maxLength={64} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="contactPerson" label="联系人">
                <Input maxLength={64} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="contactPhone"
                label="联系电话"
                rules={[
                  {
                    pattern: /^(1[3-9]\d{9}|0\d{2,3}-?\d{7,8}(-\d{1,6})?)$/,
                    message: '手机号或含区号座机（如 0571-88888888）'
                  }
                ]}
              >
                <Input maxLength={20} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="invoiceType" label="发票类型">
                <Select
                  allowClear
                  placeholder="选择票种"
                  options={[
                    { value: 'SPECIAL_VAT', label: '增值税专用发票' },
                    { value: 'GENERAL_VAT', label: '增值税普通发票' },
                    { value: 'ELECTRONIC', label: '电子发票' }
                  ]}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="taxRate" label="税率（%）" extra="按百分数录入：13 表示 13%">
                <InputNumber min={0} max={100} step={1} precision={2} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="useOuterWarehouse" label="使用外部仓" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="apiEnabled" label="接口对接" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="address" label="地址">
                <Input maxLength={255} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="remark" label="备注">
                <Input.TextArea rows={2} maxLength={255} showCount />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      <Modal
        title={current ? `开通门户账号 · ${current.supplierName}` : '开通门户账号'}
        open={accountOpen}
        onCancel={() => setAccountOpen(false)}
        okText="开通"
        cancelText="取消"
        confirmLoading={openAccount.isPending}
        onOk={() => accountForm.submit()}
        destroyOnHidden
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="登录账号由系统自动生成"
          description="按「监管单位机构号 + 4 位序号」生成，开户后回显；同时自动绑定该供应商主体，供应商门户仅能查看自己的单据（DR-8.14）。"
        />
        <Form<SupplierAccountRequest>
          form={accountForm}
          layout="vertical"
          onFinish={(values) => openAccount.mutate(values)}
        >
          <Form.Item name="realName" label="姓名" extra="缺省取供应商名称">
            <Input maxLength={32} />
          </Form.Item>
          <Form.Item
            name="mobile"
            label="手机号"
            rules={[{ pattern: /^1[3-9]\d{9}$/, message: '手机号格式不正确' }]}
            extra="选填，可用于手机号登录"
          >
            <Input maxLength={11} placeholder="11 位手机号" />
          </Form.Item>
          <Form.Item
            name="email"
            label="邮箱"
            rules={[
              { required: true, message: '请输入邮箱' },
              { type: 'email', message: '邮箱格式不正确' }
            ]}
            extra="必填，用于供应商自助重设密码"
          >
            <Input maxLength={128} />
          </Form.Item>
          <Form.Item
            name="password"
            label="初始口令"
            rules={[
              { required: true, message: '请设置初始口令' },
              { min: 8, message: '口令不少于 8 位' }
            ]}
            extra="不少于 8 位；保存后 90 天内有效"
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={admissionTarget ? `准入审核 · ${admissionTarget.supplierName}` : '准入审核'}
        open={admissionTarget !== null}
        onCancel={() => setAdmissionTarget(null)}
        okText="提交"
        cancelText="取消"
        confirmLoading={admit.isPending}
        onOk={() => admissionForm.submit()}
        destroyOnHidden
      >
        <Form
          form={admissionForm}
          layout="vertical"
          onFinish={(values) =>
            admissionTarget &&
            admit.mutate(
              { id: admissionTarget.id, status: values.status, remark: values.remark },
              { onSuccess: () => setAdmissionTarget(null) }
            )
          }
        >
          <Form.Item name="status" label="目标状态" rules={[{ required: true }]}>
            <Select
              options={[
                { value: 'PENDING', label: '待准入' },
                { value: 'ACTIVE', label: '合作中（通过）' },
                { value: 'SUSPENDED', label: '暂停合作' },
                { value: 'BLACKLIST', label: '黑名单' },
                { value: 'TERMINATED', label: '终止合作' }
              ]}
            />
          </Form.Item>
          <Form.Item
            name="remark"
            label="原因 / 审批意见"
            rules={[{ required: false }]}
            extra="列入黑名单必须填写原因"
          >
            <Input.TextArea rows={3} maxLength={255} showCount />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={editingQual ? '编辑资质' : '新增资质'}
        open={qualOpen}
        onCancel={() => setQualOpen(false)}
        okText="保存"
        cancelText="取消"
        confirmLoading={saveQual.isPending}
        onOk={() => qualForm.submit()}
        destroyOnHidden
      >
        <Form form={qualForm} layout="vertical" onFinish={(values) => saveQual.mutate(values)}>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="qualType" label="资质类型" rules={[{ required: true, message: '必填' }]}>
                <Input maxLength={64} placeholder="如 医疗器械经营许可证" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="qualNo" label="证书号">
                <Input maxLength={64} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="range" label="发证日 ~ 到期日" rules={[{ required: true, message: '请选择有效期' }]}>
                <DatePicker.RangePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="remindDays" label="提前提醒（天）">
                <InputNumber min={1} max={365} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="fileUrl" label="证照文件 URL">
                <Input maxLength={255} placeholder="OSS / COS 链接" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </Card>
  );
}
